import type { InterviewTemplate } from "./types";

/**
 * The default template: a family wellness check-in for a rural community,
 * covering nutrition, physical activity, child sleep, and food security.
 *
 * Six askable fields, in this exact order — order is semantically meaningful
 * (it drives ask order via completion.ts and is part of what
 * Fieldnote::TemplateDigest binds a continuity token to; see §7).
 *
 * Plus `notes_verbatim`: append-only, never asked, type `verbatim` so
 * validate.ts never coerces it (§8 Provenance — verbatim text is preserved
 * unchanged, in the participant's own language).
 *
 * `followup_needed` is deliberately NOT a FieldSpec here. It is Rails-derived
 * (FollowupPolicy, §5) and never asked, which puts it in the same ownership
 * class as the safety verdict — and safety isn't modeled as a field either.
 * Both live in their own InterviewState slices (`state.followup`,
 * `state.safety`), never in `state.fields`, so nothing in the
 * normalize/validate/merge pipeline can ever touch them. export.ts reads
 * `state.followup` directly for the `followup_needed` / `followup_reasons`
 * CSV columns (§8 export.ts lists them as "plus" columns, not part of the
 * per-field `__source`/`__quote`/`__confidence` pattern precisely because
 * they never went through that pipeline).
 */
export const familyWellnessTemplate: InterviewTemplate = {
  id: "family_wellness",
  instructions:
    "You are a warm, patient interviewer for a family wellness check-in run by a community health worker. " +
    "Ask one short question at a time. Never ask about more than one field per turn. " +
    "Only extract a value when the participant's own words support it with a verbatim quote. " +
    "If an answer is vague, ask a brief, kind follow-up rather than guessing.",
  fields: [
    {
      id: "fruit_veg_servings_per_day",
      label: {
        en: "Fruit & vegetable servings per day",
        es: "Porciones de frutas y verduras al día",
      },
      type: "integer",
      required: true,
      askable: true,
      min: 0,
      max: 20,
      promptHints: {
        en: [
          "About how many servings of fruits and vegetables does your family eat on a typical day?",
          "On a normal day, how many fruit or veggie servings would you say everyone gets, combined?",
        ],
        es: [
          "¿Aproximadamente cuántas porciones de frutas y verduras come su familia en un día típico?",
          "En un día normal, ¿cuántas porciones de frutas o verduras diría que comen entre todos?",
        ],
      },
    },
    {
      id: "sugary_drinks_per_week",
      label: {
        en: "Sugary drinks per week",
        es: "Bebidas azucaradas por semana",
      },
      type: "integer",
      required: true,
      askable: true,
      min: 0,
      max: 70,
      promptHints: {
        en: [
          "How many sugary drinks — soda, juice drinks, sports drinks — would you say the family has in a week?",
          "About how often do sugary drinks come up in a week for your household?",
        ],
        es: [
          "¿Cuántas bebidas azucaradas — refrescos, jugos, bebidas deportivas — diría que toma la familia en una semana?",
          "¿Con qué frecuencia hay bebidas azucaradas en su casa durante la semana?",
        ],
      },
    },
    {
      id: "physical_activity_days",
      label: {
        en: "Physical activity days per week",
        es: "Días de actividad física por semana",
      },
      type: "integer",
      required: true,
      askable: true,
      min: 0,
      max: 7,
      promptHints: {
        en: [
          "In a typical week, how many days does everyone get some physical activity or play outside?",
          "About how many days a week does everyone get moving?",
        ],
        es: [
          "En una semana típica, ¿cuántos días hacen algo de actividad física o juegan afuera?",
          "¿Aproximadamente cuántos días a la semana se mueven un poco?",
        ],
      },
    },
    {
      id: "child_bedtime_consistency",
      label: {
        en: "Child bedtime consistency",
        es: "Consistencia de la hora de dormir del niño",
      },
      type: "enum",
      required: true,
      askable: true,
      options: ["consistent", "mostly", "irregular"],
      synonyms: {
        consistent: [
          "consistent",
          "same time every night",
          "always the same",
          "constante",
          "siempre la misma hora",
        ],
        mostly: ["mostly", "usually", "most nights", "casi siempre", "la mayoría de las noches"],
        irregular: [
          "irregular",
          "varies",
          "all over the place",
          "no fijo",
          "varía",
          "a veces",
        ],
      },
      promptHints: {
        en: [
          "Does your child tend to go to bed around the same time each night, or does it vary a lot?",
          "How consistent is bedtime for your child?",
        ],
        es: [
          "¿Su hijo/a suele acostarse más o menos a la misma hora cada noche, o varía mucho?",
          "¿Qué tan constante es la hora de dormir de su hijo/a?",
        ],
      },
    },
    {
      id: "food_insecurity_flag",
      label: {
        en: "Food insecurity",
        es: "Inseguridad alimentaria",
      },
      type: "boolean_with_notes",
      required: true,
      askable: true,
      promptHints: {
        en: [
          "In the past month, has there been a time when you worried food would run out before you had money to buy more?",
          "Has getting enough food been a struggle for your family lately?",
        ],
        es: [
          "En el último mes, ¿ha habido algún momento en que le preocupara que la comida se acabara antes de tener dinero para comprar más?",
          "¿Ha sido difícil conseguir suficiente comida para su familia últimamente?",
        ],
      },
    },
    {
      id: "barriers_mentioned",
      label: {
        en: "Barriers mentioned",
        es: "Obstáculos mencionados",
      },
      type: "string_array",
      required: false,
      askable: true,
      promptHints: {
        en: [
          "Is there anything that makes healthy eating or staying active harder for your family right now?",
          "What gets in the way, if anything?",
        ],
        es: [
          "¿Hay algo que dificulte comer sano o mantenerse activos para su familia en este momento?",
          "¿Qué se interpone en el camino, si acaso?",
        ],
      },
    },
    {
      id: "notes_verbatim",
      label: {
        en: "Verbatim notes",
        es: "Notas textuales",
      },
      type: "verbatim",
      required: false,
      askable: false,
      promptHints: { en: [], es: [] },
    },
  ],
};
